import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import api from "../../services/api";
import { format } from "date-fns";

// Every call answered by zona-voice (866 support/pitch line, 786 Concierge
// demo line, outbound venue demos) once it ends -- durable copy of what used
// to live only on the zona-voice Fly box's local disk, wiped every redeploy.
const VoiceCalls = () => {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const records = 25;

  const fetchCalls = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/admin/voice-calls`, {
        params: { page, records, ...(brand ? { brand } : {}) },
      });
      setCalls(data?.data || []);
      setTotal(data?.total || 0);
    } catch (err) {
      console.error("Failed to fetch voice calls:", err);
      setCalls([]);
    } finally {
      setLoading(false);
    }
  }, [page, brand]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      return format(new Date(d), "MMM d, yyyy h:mm a");
    } catch {
      return "—";
    }
  };

  const formatDuration = (started, ended) => {
    if (!started || !ended) return "—";
    const secs = Math.max(0, Math.round((new Date(ended) - new Date(started)) / 1000));
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const BRAND_LABELS = {
    zona: { label: "866 · Support/Pitch", color: "#0A3FD6" },
    concierge_demo: { label: "786 · Concierge Demo", color: "#4F30EA" },
    venue_demo: { label: "Venue Demo (outbound)", color: "#6B7280" },
  };

  const renderBrand = (b) => {
    const meta = BRAND_LABELS[b] || { label: b, color: "#6B7280" };
    return (
      <span style={{ background: meta.color, color: "#fff", fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 999, whiteSpace: "nowrap" }}>
        {meta.label}
      </span>
    );
  };

  const filtered = calls.filter((c) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (c.caller_number || "").toLowerCase().includes(s) || (c.call_id || "").toLowerCase().includes(s);
  });

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content" style={{ padding: 24 }}>
          <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
            <div>
              <h1 style={{ margin: 0, color: "#fff" }}>Voice Calls</h1>
              <p style={{ margin: "4px 0 0", color: "rgba(255,255,255,0.5)", fontSize: 13 }}>
                Every call on ZONA's lines (Dan on 866, Jen on 786), with transcript and playback where audio was captured.
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="text"
                placeholder="Search caller number, call ID…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 13, outline: "none", width: 240 }}
              />
              <select
                value={brand}
                onChange={(e) => { setPage(1); setBrand(e.target.value); }}
                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 13, outline: "none" }}
              >
                <option value="">All lines</option>
                <option value="zona">866 · Support/Pitch</option>
                <option value="concierge_demo">786 · Concierge Demo</option>
                <option value="venue_demo">Venue Demo (outbound)</option>
              </select>
            </div>
          </div>

          <div style={{ width: "100%", background: "rgba(255,255,255,0.03)", borderRadius: 12, border: "1px solid rgba(255,255,255,0.06)", overflowX: "auto", overflowY: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", color: "#fff", minWidth: 800 }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.04)" }}>
                  <th style={th}>Line</th>
                  <th style={th}>Caller</th>
                  <th style={th}>Started</th>
                  <th style={th}>Duration</th>
                  <th style={th}>Turns</th>
                  <th style={th}>Audio</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} style={{ ...td, textAlign: "center", padding: 32, opacity: 0.6 }}>No calls yet.</td></tr>
                ) : (
                  filtered.map((c) => (
                    <tr
                      key={c.call_id}
                      onClick={() => navigate(`/voice-calls/${c.call_id}`)}
                      style={{ cursor: "pointer", borderTop: "1px solid rgba(255,255,255,0.05)" }}
                    >
                      <td style={td}>{renderBrand(c.brand)}</td>
                      <td style={td}>{c.caller_number || <span style={{ opacity: 0.4 }}>—</span>}</td>
                      <td style={td}>{formatDate(c.started)}</td>
                      <td style={td}>{formatDuration(c.started, c.ended)}</td>
                      <td style={td}>{c.turn_count}{c.turn_count === 0 && <span style={{ opacity: 0.5 }}> (silent)</span>}</td>
                      <td style={td}>{c.has_audio ? "▶ Play" : <span style={{ opacity: 0.4 }}>—</span>}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {total > records && (
            <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 16, color: "#fff" }}>
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={pageBtn(page <= 1)}>Prev</button>
              <span style={{ fontSize: 13, alignSelf: "center", opacity: 0.7 }}>
                Page {page} of {Math.ceil(total / records)}
              </span>
              <button type="button" disabled={page >= Math.ceil(total / records)} onClick={() => setPage((p) => p + 1)} style={pageBtn(page >= Math.ceil(total / records))}>Next</button>
            </div>
          )}
        </article>
      </div>
    </main>
  );
};

const th = { textAlign: "left", padding: "12px 14px", fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", color: "rgba(255,255,255,0.6)" };
const td = { padding: "12px 14px", fontSize: 13, verticalAlign: "middle" };
const pageBtn = (disabled) => ({
  background: disabled ? "transparent" : "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: disabled ? "rgba(255,255,255,0.3)" : "#fff",
  padding: "6px 14px",
  borderRadius: 8,
  fontSize: 13,
  cursor: disabled ? "default" : "pointer",
});

export default VoiceCalls;
